import React, {useState, useEffect,} from "react";
import {useNavigate, useLocation} from "react-router";
import Cookies from "js-cookie";
import "./indexPageFormComponent.css"
import {
  Button,
  Card,
  Checkbox,
  FlexItem, FlexLayout,
  FormField,
  FormFieldHelperText,
  FormFieldLabel,
  H1,
  Input, Link,
  StackLayout
} from "@salt-ds/core";
import Constants from "../../../components/common/Constants";
import {HiddenIcon, VisibleIcon} from "@salt-ds/icons";
import {validateDuplicateUserId} from '../../../api/ForgotPasswordApi';
import {login, success} from '../../../api/LoginApi';
import PasswordExpiringSoonModal from "../../../components/common/PasswordExpiringSoonModal";
import {storeToken} from "../../../common/authTokenUtil";
import {jsonSignature} from "../../../components/common/mfaUtils/logonmfa";
import setDeviceId from "../../../components/common/mfaUtils/deviceId";

export default function IndexPageFormComponent(props) {

  const navigate = useNavigate();
  const location = useLocation();
  const DEVICE_REGISTRATION_REQUIRED = "secauth.required"
  const [isError, setIsError] = useState(false);
  // Fix the nested formData structure by extracting the inner formData if it exists, otherwise use location.state or initialize empty
  const [formData, setFormData] = useState(() => {
    if (location.state?.formData) {
      // Use formData from location.state if available
      return location.state.formData;
    } else if (location.state) {
      // If location.state exists but doesn't have formData, use location.state directly
      return location.state;
    } else {
      // Default initialization
      return {userId: "", password: "", orgId: "", rememberOrganization: false};
    }
  });
  const [passwordExpiryFormData, setPasswordExpiryFormData] = useState({daysUntilNextReminder: 90});
  const [lastUserId, setLastUserId] = useState('');
  const [orgIdVisible, setOrgIdVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    userId: false,
    email: {required: false, invalid: false},
    orgId: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {

    if (location.state?.formData?.userId) {
      setFormData(prevData => ({
        ...prevData,
        userId: location.state.formData.userId
      }));
      // If we already have a userId, check if we need to show orgId field
      if (location.state.formData.userId.trim() !== '') {
        validateDuplicateUserID(location.state.formData.userId);
      }
    }

    if (location.state?.formData?.orgId) {
      setFormData(prevData => ({
        ...prevData,
        orgId: location.state.formData.orgId
      }));
    }
  }, []);

  const validateDuplicateUserID = async (userId) => {

    if(userId !== "" && lastUserId !== null && userId === lastUserId){
      setOrgIdVisible(true);
      return true; // Return true if orgId is required
    }
    if (userId !== null && userId !== '' && userId.length > 0 && (lastUserId == null || userId !== lastUserId)) {
      try {
        const response = await validateDuplicateUserId(userId);
        if (response.data) {

          if (response.organization) {
            setFormData(prevData => ({
              ...prevData,
              orgId: response.organization,
              rememberOrganization: true
            }));
          }
          setOrgIdVisible(true);
          setLastUserId(userId);
          return true; // Return true if orgId is required
        } else {
          setFormData(prevState => ({
            ...prevState,
            orgId: '',
            rememberOrganization: false
          }))
          setOrgIdVisible(false);
          return false; // Return false if orgId is not required
        }
      } catch {
        setFormData(prevState => ({
          ...prevState,
          orgId: '',
          rememberOrganization: false
        }))
        setOrgIdVisible(false);
        return false; // Return false on error
      }
    }
    return false; // Return false if userId is empty
  };

  const handleChange = (event) => {
    const {name, value, checked, type} = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked :  value
    }));

    //Clear error when user begins to type
    if (name === 'userId') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        userId: false,
      }));
      setOrgIdVisible(false);
    } else if (name === 'password') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: false,
      }));
    } else if (name === 'orgId') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        orgId: false,
      }));
    }
  };

  async function isPasswordExpiring(data) {
    const response = await success(data.organizationNumber, data.loginName
    );
    return response.data;
  }


  function isDeviceRegistraionRequired(data) {
    return data.siteMinderResponseCode === DEVICE_REGISTRATION_REQUIRED;

  }

  const isValidUserId = (userId) => {
    const userIdRegEx = /^[a-zA-Z0-9]+$/;
    return userIdRegEx.test(userId);
  }

  //Validation for empty fields
  const validateFormData = (orgIdRequired) => {

    return {
      userId: !formData.userId || formData.userId.trim() === '' || !isValidUserId(formData.userId || ''),
      password: !formData.password || formData.password.trim() === '',
      orgId: (!formData.orgId || formData.orgId.trim() === '')
    };
  };

  const handleSuccessfulLogin = async (data, deviceSignature) => {
    const authJwtToken = data.jwtToken;
    storeToken(authJwtToken);
    sessionStorage.setItem('loginName', data.loginName);
    sessionStorage.setItem('orgName', data.organizationNumber);

    if (data.newUser === true) {
      handleNewUser(data);
    }else if ( (data.newUser === false && data.siteMinderResponseCode === "password.expired") || data.secretPhraseResetRequired ) {
      navigate(Constants.forcePasswordChange, { state: { from: Constants.login } });
    }else {
      await handleExistingUser(data, deviceSignature);
    }
  };

  const handleNewUser = (data) => {
    if (data.csrf !== null) {
      sessionStorage.setItem(data.csrf.headerName, data.csrf.token);
    }
    navigate(Constants.firstTimeLogInSetup, { state: { from: Constants.login } });
  };

  const handleExistingUser = async (data, deviceSignature) => {
    if (isDeviceRegistraionRequired(data)) {
      navigate(Constants.registerComputer, {
        state: {
          userId: formData.userId,
          orgId: formData.orgId,
          deviceSignature: deviceSignature,
          secretPhrase: formData.password,
          from: "loginPage"
        }
      });
    } else {
      const passwordExpiryData = await isPasswordExpiring(data);
      if (passwordExpiryData.reminderRequired) {
        setPasswordExpiryFormData({ ...passwordExpiryData });
        props.fillAndOpenModel(
          <PasswordExpiringSoonModal
            closeAndClearModal={props.closeAndClearModal}
            fillAndOpenModel={props.fillAndOpenModel}
            passwordExpiryFormData={passwordExpiryData}
            setShowRedirect={props.setShowRedirect}
            useTargetRedirect={true}
          />
        );
      } else {
        props.setShowRedirect(true);
      }
    }
  };

  const handleSubmit = async (event) => {
    setIsSubmitting(true);
    props.setIsLoading(true);
    event.preventDefault();

    // Autofill fix: If orgId is not visible, check if it should be before submitting
    let orgIdRequired = orgIdVisible;
    if (!orgIdVisible) {
      orgIdRequired = await validateDuplicateUserID(formData.userId);
      // If orgId is required, block submission and prompt user to fill it
      if (orgIdRequired) {
        // stop submitting form and show orgId field
        setIsSubmitting(false);
        props.setIsLoading(false);
        return;
      }
    }

    const newErrors = validateFormData(orgIdRequired);
    setErrors(newErrors);

    const deviceSignature = jsonSignature();
    sessionStorage.setItem("deviceSignature", deviceSignature);
    const {deviceId} = setDeviceId();
    const csrf = Cookies.get('XSRF-TOKEN');

    //Submit the form
    if (!newErrors.userId && !newErrors.password && (!orgIdRequired || !newErrors.orgId)) {
      try {
        const response = await login(
            formData,
          formData.orgId,
          formData.userId,
          formData.password,
          formData.rememberOrganization,
          deviceId,
          deviceSignature,
          csrf
        );

        if (response.data) {
            await handleSuccessfulLogin(response.data, deviceSignature);
        } else {
            props.setIsLoading(false);
            setIsSubmitting(false);
            handleLoginError(response);
        }
      } catch {
          props.setIsLoading(false);
          setIsSubmitting(false);
      } finally {
          props.setIsLoading(false);
      }
    } else {
      props.setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleLoginError = (response) => {
    if (response.userLoginStatus === "LOCKED" || response.userLoginStatus === "LOCKPWDEXP") {
      sessionStorage.setItem("previousPage", Constants.login);
      props.showErrorOnPage(Constants.lockedUserErrorMessage);
    } else if (response.userLoginStatus === "INACTIVE") {
      props.showErrorOnPage(Constants.inactiveUserErrorMessage);
    } else if (
        (response.userLoginStatus === "ACTIVE" || response.userLoginStatus === "PASSWORDEXPIRED") &&
        response.failedLoginCount > 0
    ) {
      const availableLoginAttempts = 3 - response.failedLoginCount;
      props.showErrorOnPage(Constants.invalidUserCredentialsErrorMessage(availableLoginAttempts));
    } else if (response.error && response.error.detail){
      if(response.error.detail === "LOCKED_LOGIN_AUTHENTICATION_ERROR") {
        props.showErrorOnPage(Constants.lockedUserErrorMessage);
      }else if(response.error.detail === "INACTIVE_LOGIN_AUTHENTICATION_ERROR") {
        props.showErrorOnPage(Constants.inactiveUserErrorMessage);
      }else if(response.error.detail === "LOCKED_AND_EXPIRED_LOGIN_AUTHENTICATION_ERROR") {
        props.showErrorOnPage(Constants.lockedUserErrorMessage);
      }else {
        props.showErrorOnPage(response.error.detail);
      }

    } else {
      props.showErrorOnPage(Constants.incorrectUserCredentialsErrorMessage);
    }
    setFormData(prevState => ({
      ...prevState,
      userId: "",
      password: "",
      orgId: "",
      rememberOrganization: false
    }));
    setOrgIdVisible(false);
    setIsError(true);
  };

  const handleKeyPress = (event, redirectPath) => {
    if (event.key === "Enter" || event.key === " ") {
      navigate(redirectPath, { state: { formData, from: Constants.login } });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const openFraudAlertsLink = () => {
    window.open(
        "https://ccportal.jpmorgan.com/",
        "_blank",
        "noopener,noreferrer"
    );
  };

  return (
    <Card className="index-page-card">
      <form autoComplete="off" data-testid="index-page-form-submit" onSubmit={handleSubmit}>
        <fieldset disabled={isSubmitting} style={{border:0, padding:0, margin:0}}>
        <StackLayout gap={2}>
          <FlexItem>
            <H1>{Constants.homePageLoginCardHeading}</H1>
          </FlexItem>
          <FlexItem>
            <FormField className={"form-fields"} validationStatus={errors.userId ? "error" : undefined}>
              <FormFieldLabel htmlFor="userId">{Constants.userIdLabel}</FormFieldLabel>
              <Input id="userId" value={formData.userId} onChange={handleChange}
                onBlur={() => validateDuplicateUserID(formData.userId)} inputProps={{ name: "userId", minLength: 6, maxLength: 20, "aria-required": "true",
                "aria-describedby": errors.userId ? "userId-error" : undefined,
                onKeyPress: (e) => {
                  if (!/^[a-zA-Z0-9]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}}/>
              {errors.userId && (<FormFieldHelperText id="userId-error" role="alert">{Constants.requiredFieldError}</FormFieldHelperText>)}
            </FormField>
          </FlexItem>
          <FlexItem>
            <FormField className={"form-fields"} validationStatus={errors.password ? "error" : undefined}>
              <FormFieldLabel>{Constants.passwordLabel}</FormFieldLabel>
              <div className="password-input">
                <Input
                  inputProps={{
                    ...(showPassword ? {type: "text"} : {type: "password"}),
                    name: 'password',
                    maxLength: "25",
                    "aria-required": "true",
                    "aria-describedby": errors.password ? "password-error" : undefined
                  }} value={formData.password}
                  onChange={handleChange} />

                {
                  errors.password ? <></> :
                    showPassword ?
                      <VisibleIcon tabIndex={0} data-testid="visible-icon" style={{marginLeft: "-2rem", height: "2.2rem", width: "1.2rem"}}
                                   onClick={togglePasswordVisibility}
                                   onKeyPress={togglePasswordVisibility}
                                   role="button"
                                   aria-label="Hide password"
                      />
                      : <HiddenIcon tabIndex={0} data-testid="hidden-icon" style={{marginLeft: "-2rem", height: "2.2rem", width: "1.2rem"}}
                                    onClick={togglePasswordVisibility}
                                    onKeyPress={togglePasswordVisibility}
                                    role="button"
                                    aria-label="Show password"
                      />
                }
              </div>
              {errors.password && (
                <FormFieldHelperText id="password-error" role="alert">
                  {Constants.requiredFieldError}
                </FormFieldHelperText>
              )}
              {!errors.password && (
                <FormFieldHelperText>
                  {Constants.passwordHelper}
                </FormFieldHelperText>
              )}
              <FormFieldHelperText>{}</FormFieldHelperText>
            </FormField>
            {orgIdVisible && (
              <FormField className={"form-fields"} validationStatus={errors.orgId ? "error" : undefined}>
                <FormFieldLabel htmlFor="orgId">{Constants.orgIdLabel}</FormFieldLabel>
                <Input
                  id="orgId"
                  inputProps={{name: "orgId", maxLength:7, "aria-required": "true", "aria-describedby": errors.orgId ? "orgId-error" : undefined,}}
                  value={formData.orgId}
                  onChange={handleChange}
                />
                {errors.orgId && <FormFieldHelperText id="orgId-error" role="alert">{Constants.requiredFieldError}</FormFieldHelperText>}
              </FormField>
            )}
            {orgIdVisible && (
              <FormField className={"form-fields"}>
                <Checkbox checked={formData.rememberOrganization}  label="Remember me" name={"rememberOrganization"} value={"rememberOrganization"} onChange={handleChange} />
              </FormField>
            )}

          </FlexItem>

          <FlexItem>
            <FlexLayout gap={0} className={"index-button-cover"}>
              <FlexItem>
                <Button id={"LoginButton"} sentiment="accented" appearance="solid" type="submit" onClick={handleSubmit}>
                  Log IN
                </Button>
              </FlexItem>
              <FlexItem>
                <Link
                  tabIndex={0}
                  onKeyPress={(e) => handleKeyPress(e, Constants.forgotUserOrOrgIdEmail)}
                  onClick={() => navigate(Constants.forgotUserOrOrgIdEmail,{state: {formData, from: Constants.login}})}>
                  {Constants.forgotUserIdOrgIdLabel}
                </Link>
              </FlexItem>
              <FlexItem>
                <span className="index-link-separator">|</span>
              </FlexItem>
              <FlexItem>
                <Link
                  tabIndex={0}
                  onKeyPress={(e) => handleKeyPress(e, Constants.forgotPassword)}
                  onClick={() => navigate(Constants.forgotPassword,{state: {formData, from: Constants.login}})}>
                  {Constants.homePageLoginCardForgotPasswordLabel}
                </Link>
              </FlexItem>
            </FlexLayout>
          </FlexItem>

          <FlexItem>
            <FlexLayout className={"index-register-cover"}>
              <FlexItem>
                <Card variant={"secondary"} className={"register-bg"}>
                  <FlexLayout gap={0} align={"center"} justify={"center"}>
                    <FlexItem className="no-online-profile-text">
                      No Online Profile?
                    </FlexItem>
                    <FlexItem>
                      <Button id={"registerButton"} sentiment="accented" appearance="bordered" type="button"
                        onClick={() => navigate('/profileRegistration',{state: {formData, from: '/login'}})}>
                        Register Card
                      </Button>
                    </FlexItem>
                  </FlexLayout>
                </Card>
              </FlexItem>
              <FlexItem>
                <Link
                  tabIndex={0}
                  onKeyPress={openFraudAlertsLink}
                  onClick={(e) => {
                    e.preventDefault();
                    openFraudAlertsLink();
                  }} sentiment="accented" appearance="transparent" className={"index-link fraud-button"}>
                  {Constants.homePageLoginCardFraudLabel}
                </Link>
              </FlexItem>
            </FlexLayout>
          </FlexItem>

        </StackLayout>
        </fieldset>
      </form>
    </Card>
  );

}